import { processPaginationHeaders } from "../../../utils/helpers/kgPagination.utils";
import { AbstractKgPaginatedResponse } from "../../../utils/types/pagination.types";
import { searchV2Api } from "../../searchV2/api/searchV2Api.api";
import type {
  GetDataConnectorsApiArg,
  GetDataConnectorsApiResponse as GetDataConnectorsApiResponseOrig,
  GetDataConnectorsByDataConnectorIdApiArg,
  GetDataConnectorsByDataConnectorIdApiResponse,
  GetDataConnectorsByDataConnectorIdDepositsApiArg,
  GetDataConnectorsByDataConnectorIdDepositsApiResponse as GetDataConnectorsByDataConnectorIdDepositsApiResponseOrig,
  GetDataConnectorsByDataConnectorIdSecretsApiArg,
  GetDataConnectorsByDataConnectorIdSecretsApiResponse,
} from "./data-connectors.api";
import { dataConnectorsApi as api } from "./data-connectors.api";

export interface GetDataConnectorsApiResponse extends AbstractKgPaginatedResponse {
  dataConnectors: GetDataConnectorsApiResponseOrig;
}

export interface GetDataConnectorsByDataConnectorIdDepositsApiResponse extends AbstractKgPaginatedResponse {
  deposits: GetDataConnectorsByDataConnectorIdDepositsApiResponseOrig;
}

interface GetDataConnectorsListByDataConnectorIdsApiArg {
  dataConnectorIds: GetDataConnectorsByDataConnectorIdApiArg["dataConnectorId"][];
}

type GetDataConnectorsListByDataConnectorIdsApiResponse = Record<
  string,
  GetDataConnectorsByDataConnectorIdApiResponse
>;

interface GetDataConnectorListSecretsApiArg {
  dataConnectorIds: GetDataConnectorsByDataConnectorIdSecretsApiArg["dataConnectorId"][];
}

type GetDataConnectorListSecretsApiResponse = Record<
  string,
  GetDataConnectorsByDataConnectorIdSecretsApiResponse
>;

const injectedApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getDataConnectorsPaged: builder.query<
      GetDataConnectorsApiResponse,
      GetDataConnectorsApiArg
    >({
      query: (queryArg) => ({
        url: "/data_connectors",
        params: {
          namespace: queryArg.params?.namespace,
          page: queryArg.params?.page,
          per_page: queryArg.params?.per_page,
        },
      }),
      transformResponse: (response, meta, queryArg) => {
        const dataConnectors = response as GetDataConnectorsApiResponseOrig;
        const headers = meta?.response?.headers;
        const headerResponse = processPaginationHeaders(
          headers,
          queryArg.params == null
            ? {}
            : { page: queryArg.params.page, perPage: queryArg.params.per_page },
          dataConnectors,
        );

        return {
          dataConnectors,
          page: headerResponse.page,
          perPage: headerResponse.perPage,
          total: headerResponse.total,
          totalPages: headerResponse.totalPages,
        };
      },
    }),
    getDataConnectorsByDataConnectorIdDeposits: builder.query<
      GetDataConnectorsByDataConnectorIdDepositsApiResponse,
      GetDataConnectorsByDataConnectorIdDepositsApiArg
    >({
      query: (queryArg) => ({
        url: `/data_connectors/${queryArg.dataConnectorId}/deposits`,
        params: {
          page: queryArg.params?.page,
          per_page: queryArg.params?.per_page,
        },
      }),
      transformResponse: (response, meta, queryArg) => {
        const deposits =
          response as GetDataConnectorsByDataConnectorIdDepositsApiResponseOrig;

        const headers = meta?.response?.headers;
        const headerResponse = processPaginationHeaders(
          headers,
          queryArg.params == null
            ? {}
            : { page: queryArg.params.page, perPage: queryArg.params.per_page },
          deposits,
        );

        return {
          deposits,
          page: headerResponse.page,
          perPage: headerResponse.perPage,
          total: headerResponse.total,
          totalPages: headerResponse.totalPages,
        };
      },
    }),
    getDataConnectorsListByDataConnectorIds: builder.query<
      GetDataConnectorsListByDataConnectorIdsApiResponse,
      GetDataConnectorsListByDataConnectorIdsApiArg
    >({
      async queryFn(queryArg, _api, _options, fetchWithBQ) {
        const { dataConnectorIds } = queryArg;
        const result: GetDataConnectorsListByDataConnectorIdsApiResponse = {};
        const promises = dataConnectorIds.map((dataConnectorId) =>
          fetchWithBQ(`/data_connectors/${dataConnectorId}`),
        );
        const responses = await Promise.all(promises);
        for (let i = 0; i < dataConnectorIds.length; i++) {
          const dataConnectorId = dataConnectorIds[i];
          const response = responses[i];
          if (response.error) return response;
          result[dataConnectorId] =
            response.data as GetDataConnectorsByDataConnectorIdApiResponse;
        }
        return { data: result };
      },
    }),
    getDataConnectorsListSecrets: builder.query<
      GetDataConnectorListSecretsApiResponse,
      GetDataConnectorListSecretsApiArg
    >({
      async queryFn(queryArg, _api, _options, fetchWithBQ) {
        const { dataConnectorIds } = queryArg;
        const result: GetDataConnectorListSecretsApiResponse = {};
        const promises = dataConnectorIds.map((dataConnectorId) =>
          fetchWithBQ(`/data_connectors/${dataConnectorId}/secrets`),
        );
        const responses = await Promise.all(promises);
        for (let i = 0; i < dataConnectorIds.length; i++) {
          const dataConnectorId = dataConnectorIds[i];
          const response = responses[i];
          if (response.error) return response;
          result[dataConnectorId] =
            response.data as GetDataConnectorsByDataConnectorIdSecretsApiResponse;
        }
        return { data: result };
      },
    }),
  }),
});

const enhancedApi = injectedApi.enhanceEndpoints({
  addTagTypes: [
    "Deposits",
    "DataConnectors",
    "DataConnectorsProjectLinks",
    "DataConnectorSecrets",
  ],
  endpoints: {
    deleteDataConnectorsByDataConnectorId: {
      invalidatesTags: (_result, error) => (error ? [] : ["DataConnectors"]),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => {
          if (_arg.dataConnectorId) {
            dispatch(searchV2Api.util.invalidateTags(["SearchV2"]));
          }
        });
      },
    },
    deleteDataConnectorsByDataConnectorIdProjectLinksAndLinkId: {
      invalidatesTags: ["DataConnectorsProjectLinks"],
    },
    deleteDataConnectorsByDataConnectorIdSecrets: {
      invalidatesTags: ["DataConnectorSecrets"],
    },
    deleteDepositsByDepositId: {
      invalidatesTags: (_result, _error, { depositId }) =>
        depositId
          ? [{ id: depositId, type: "Deposits" }, "Deposits"]
          : ["Deposits"],
    },
    getDataConnectorsPaged: {
      providesTags: ["DataConnectors"],
    },
    getDataConnectorsByDataConnectorId: {
      providesTags: ["DataConnectors"],
    },
    getDataConnectorsByDataConnectorIdDeposits: {
      providesTags: ["Deposits"],
    },
    getDataConnectorsListByDataConnectorIds: {
      providesTags: ["DataConnectors"],
    },
    getDataConnectorsListSecrets: {
      providesTags: ["DataConnectorSecrets"],
    },
    getDataConnectorsByDataConnectorIdProjectLinks: {
      providesTags: ["DataConnectorsProjectLinks"],
    },
    getDataConnectorsByDataConnectorIdSecrets: {
      providesTags: ["DataConnectorSecrets"],
    },
    getDeposits: {
      providesTags: ["Deposits"],
    },
    getDepositsByDepositId: {
      providesTags: (result) =>
        result
          ? [{ id: result.id, type: "Deposits" }, "Deposits"]
          : ["Deposits"],
    },
    getNamespacesByNamespaceDataConnectorsAndSlug: {
      providesTags: ["DataConnectors"],
    },
    getNamespacesByNamespaceProjectsAndProjectDataConnectorsSlug: {
      providesTags: ["DataConnectors"],
    },
    getProjectsByProjectIdDataConnectorLinks: {
      providesTags: ["DataConnectorsProjectLinks"],
    },
    patchDataConnectorsByDataConnectorId: {
      invalidatesTags: ["DataConnectors"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        queryFulfilled.finally(() => {
          dispatch(searchV2Api.util.invalidateTags(["SearchV2"]));
        });
      },
    },
    patchDataConnectorsByDataConnectorIdSecrets: {
      invalidatesTags: ["DataConnectorSecrets"],
    },
    patchDepositsByDepositId: {
      invalidatesTags: (result) =>
        result
          ? [{ id: result.id, type: "Deposits" }, "Deposits"]
          : ["Deposits"],
    },
    postDataConnectors: {
      invalidatesTags: ["DataConnectors"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        queryFulfilled.finally(() => {
          dispatch(searchV2Api.util.invalidateTags(["SearchV2"]));
        });
      },
    },
    postDataConnectorsByDataConnectorIdProjectLinks: {
      // ? We add "DataConnectors" to force refreshing the list of DC in the project page
      invalidatesTags: ["DataConnectors", "DataConnectorsProjectLinks"],
    },
    postDataConnectorsGlobal: {
      invalidatesTags: ["DataConnectors"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        queryFulfilled.finally(() => {
          dispatch(searchV2Api.util.invalidateTags(["SearchV2"]));
        });
      },
    },
    postDeposits: {
      invalidatesTags: ["Deposits"],
    },
    postDepositsByDepositIdJob: {
      invalidatesTags: (_result, _error, { depositId }) =>
        depositId
          ? [{ id: depositId, type: "Deposits" }, "Deposits"]
          : ["Deposits"],
    },
  },
});

export { enhancedApi as dataConnectorsApi };
export const {
  // data connectors hooks
  useDeleteDataConnectorsByDataConnectorIdMutation,
  useDeleteDataConnectorsByDataConnectorIdProjectLinksAndLinkIdMutation,
  useDeleteDataConnectorsByDataConnectorIdSecretsMutation,
  useDeleteDepositsByDepositIdMutation,
  useGetDataConnectorsPagedQuery: useGetDataConnectorsQuery,
  useGetDataConnectorsByDataConnectorIdQuery,
  useGetDataConnectorsByDataConnectorIdDepositsQuery,
  useGetDataConnectorsByDataConnectorIdProjectLinksQuery,
  useGetDataConnectorsByDataConnectorIdSecretsQuery,
  useGetDataConnectorsListByDataConnectorIdsQuery,
  useGetDataConnectorsListSecretsQuery,
  useGetDepositsQuery,
  useGetDepositsByDepositIdQuery,
  useGetNamespacesByNamespaceDataConnectorsAndSlugQuery,
  useGetNamespacesByNamespaceProjectsAndProjectDataConnectorsSlugQuery,
  usePatchDataConnectorsByDataConnectorIdMutation,
  usePatchDataConnectorsByDataConnectorIdSecretsMutation,
  usePatchDepositsByDepositIdMutation,
  usePostDataConnectorsByDataConnectorIdProjectLinksMutation,
  usePostDataConnectorsMutation,
  usePostDataConnectorsGlobalMutation,
  usePostDepositsMutation,
  usePostDepositsByDepositIdJobMutation,
  useGetDataConnectorsByDataConnectorIdPermissionsQuery,
  useGetProjectsByProjectIdDataConnectorLinksQuery,
  useGetProjectsByProjectIdInaccessibleDataConnectorLinksQuery,
} = enhancedApi;
